import type { ButtonHTMLAttributes } from "react";

import { ChevronRightIcon } from "./icons";

// ChevronAction — a row's trailing text action with a chevron: "SESSION REVIEW ›",
// "RESCHEDULE ›". Eyebrow type, uppercase and tracked, in --success.
//
// At REST it is plain text: no border, no fill. The card (hairline --border on a
// --surface fill) materialises on hover only. `border-transparent` rather than no
// border, and the padding is unconditional, so the card appears without nudging the
// label a pixel. It answers `group-hover` as well as its own hover, so a row that is
// itself the click target (the portal's completed-sessions table) lights the
// affordance from anywhere on the row.
//
// One component for the portal table and the couple app's session rows, which used to
// draw two different trailing actions for the same idea.

/** The ChevronAction look as a class string, for a label INSIDE a larger clickable parent
 *  (a card that is itself the button), where a nested <button> would be invalid. Give the
 *  parent `group` so hovering anywhere on it lights the label. */
export const CHEVRON_ACTION_CLASS =
    "inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-transparent px-4 py-2 [font:var(--type-eyebrow)] uppercase [letter-spacing:var(--tracking-eyebrow)] text-success transition-colors group-hover:border-border group-hover:bg-surface hover:border-border hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2";

export interface ChevronActionProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
    label: string;
}

export function ChevronAction({ label, className = "", type = "button", ...rest }: ChevronActionProps) {
    return (
        <button
            type={type}
            {...rest}
            className={[
                CHEVRON_ACTION_CLASS,
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {label}
            <ChevronRightIcon />
        </button>
    );
}
